// Chemin: C:\gestion-copro\src\types\verification.types.ts
// Session C  : AppelFonds, Regularisation, SyntheseComptable, helpers formatMontant, detecterTrimestre
// Session 67 : Création module Vérification Comptable
// Référence : CADRAGE_MODULE_VERIFICATION_COMPTABLE_V1.md

import { Timestamp } from 'firebase/firestore';

// ============================================
// TYPES DE CONTRÔLES
// ============================================

/**
 * Types de contrôles automatiques SENTINELLE
 * Niveau 1 : Cohérence interne
 * Niveau 2 : Cohérence externe (rapprochement)
 * Niveau 3 : Alertes métier (intelligence)
 */
export type TypeControle =
  // Niveau 1 - Cohérence interne
  | 'total_vs_detail'           // Somme des lignes ≠ total affiché
  | 'appel_vs_budget'           // Montant appelé ≠ quote-part budget voté
  | 'repartition_tantiemes'     // Somme tantièmes ≠ total
  | 'doublon_facture'           // Même facture apparaît 2 fois
  | 'dates_exercice'            // Charges hors période imputées
  | 'report_nouveau'            // Incohérence clôture N-1 / ouverture N
  // Niveau 2 - Cohérence externe
  | 'facture_vs_compta'         // Montant facture ≠ ligne comptable
  | 'contrat_vs_factures'       // Facturé > Contractuel sans avenant
  | 'budget_variation'          // Variation > 20% sans explication
  | 'releve_vs_compta'          // Mouvement bancaire non retrouvé
  | 'resolution_vs_depense'     // Travaux facturés sans vote préalable
  // Niveau 3 - Alertes métier
  | 'surfacturation'            // Prix unitaire > moyenne marché
  | 'travaux_non_votes'         // Dépense > seuil sans résolution
  | 'honoraires_excessifs'      // Hors forfait > 30% du forfait annuel
  | 'fournisseur_unique'        // Même prestataire partout
  | 'provision_non_utilisee'    // Fonds collectés, travaux non réalisés
  | 'impayes_non_recouvres';    // Créances anciennes sans action

/**
 * Niveau du contrôle (pour affichage et priorisation)
 */
export type NiveauControle = 1 | 2 | 3;

/**
 * Résultat d'un contrôle
 */
export type ResultatControle = 'ok' | 'attention' | 'anomalie';

/**
 * Statut de traitement d'un contrôle par le CS
 */
export type StatutControle = 
  | 'nouveau'      // Vient d'être détecté
  | 'justifie'     // CS a validé avec explication
  | 'conteste'     // CS conteste, demande justificatif au syndic
  | 'signale_ag';  // À présenter en AG

// ============================================
// LABELS ET CONSTANTES
// ============================================

export const TYPE_CONTROLE_LABELS: Record<TypeControle, string> = {
  // Niveau 1
  total_vs_detail: 'Total vs Détail',
  appel_vs_budget: 'Appel vs Budget',
  repartition_tantiemes: 'Répartition tantièmes',
  doublon_facture: 'Doublon de facture',
  dates_exercice: 'Dates hors exercice',
  report_nouveau: 'Report à nouveau',
  // Niveau 2
  facture_vs_compta: 'Facture vs Comptabilité',
  contrat_vs_factures: 'Contrat vs Factures',
  budget_variation: 'Variation budget N/N-1',
  releve_vs_compta: 'Relevé vs Comptabilité',
  resolution_vs_depense: 'Résolution vs Dépense',
  // Niveau 3
  surfacturation: 'Surfacturation potentielle',
  travaux_non_votes: 'Travaux non votés',
  honoraires_excessifs: 'Honoraires hors forfait excessifs',
  fournisseur_unique: 'Fournisseur unique',
  provision_non_utilisee: 'Provision non utilisée',
  impayes_non_recouvres: 'Impayés non recouvrés',
};

export const TYPE_CONTROLE_NIVEAUX: Record<TypeControle, NiveauControle> = {
  // Niveau 1
  total_vs_detail: 1,
  appel_vs_budget: 1,
  repartition_tantiemes: 1,
  doublon_facture: 1,
  dates_exercice: 1,
  report_nouveau: 1,
  // Niveau 2
  facture_vs_compta: 2,
  contrat_vs_factures: 2,
  budget_variation: 2,
  releve_vs_compta: 2,
  resolution_vs_depense: 2,
  // Niveau 3
  surfacturation: 3,
  travaux_non_votes: 3,
  honoraires_excessifs: 3,
  fournisseur_unique: 3,
  provision_non_utilisee: 3,
  impayes_non_recouvres: 3,
};

export const RESULTAT_CONTROLE_LABELS: Record<ResultatControle, string> = {
  ok: 'Conforme',
  attention: 'À vérifier',
  anomalie: 'Anomalie',
};

export const RESULTAT_CONTROLE_COLORS: Record<ResultatControle, string> = {
  ok: 'bg-green-100 text-green-800',
  attention: 'bg-amber-100 text-amber-800',
  anomalie: 'bg-red-100 text-red-800',
};

export const STATUT_CONTROLE_LABELS: Record<StatutControle, string> = {
  nouveau: 'Nouveau',
  justifie: 'Justifié',
  conteste: 'Contesté',
  signale_ag: 'Signalé AG',
};

export const STATUT_CONTROLE_COLORS: Record<StatutControle, string> = {
  nouveau: 'bg-blue-100 text-blue-800',
  justifie: 'bg-green-100 text-green-800',
  conteste: 'bg-orange-100 text-orange-800',
  signale_ag: 'bg-purple-100 text-purple-800',
};

// ============================================
// ENTITÉ CONTRÔLE
// ============================================

/**
 * Un contrôle individuel dans une vérification
 * Stocké en sous-collection : verifications/{id}/controles/{controleId}
 */
export interface Controle {
  id: string;
  
  // Classification
  type: TypeControle;
  niveau: NiveauControle;
  resultat: ResultatControle;
  
  // Détail de l'anomalie
  description: string;
  regleAppliquee: string;        // Explication de la règle de contrôle
  
  // Données contextuelles (selon le type)
  montant?: number;
  montantAttendu?: number;
  ecart?: number;
  pourcentageEcart?: number;
  fournisseur?: string;
  libelle?: string;
  dateDocument?: Timestamp;
  documentSourceId?: string;     // Référence au document GED analysé
  
  // Suivi par le CS
  statut: StatutControle;
  noteCS?: string;               // Commentaire du CS
  justificatifUrl?: string;      // Pièce jointe justificative
  
  // Actions liées
  courrierId?: string;           // Si demande de justificatif envoyée
  affaireId?: string;            // Si affaire créée
  
  // Métadonnées
  dateDetection: Timestamp;
  dateTraitement?: Timestamp;
  traitePar?: string;            // userId
  traiteParNom?: string;
}

// ============================================
// ENTITÉ VÉRIFICATION
// ============================================

/**
 * Statut global d'une vérification
 */
export type StatutVerification = 'en_cours' | 'termine' | 'archive';

export const STATUT_VERIFICATION_LABELS: Record<StatutVerification, string> = {
  en_cours: 'En cours',
  termine: 'Terminé',
  archive: 'Archivé',
};

export const STATUT_VERIFICATION_COLORS: Record<StatutVerification, string> = {
  en_cours: 'bg-blue-100 text-blue-800',
  termine: 'bg-green-100 text-green-800',
  archive: 'bg-gray-100 text-gray-800',
};

/**
 * Entité principale Vérification Comptable
 * Collection : coproprietes/{coproprieteId}/verifications/{verificationId}
 */
export interface Verification {
  id: string;
  coproprieteId: string;
  
  // Identification
  exercice: string;              // "2025", "2024", etc.
  titre?: string;                // Optionnel: "Contrôle pré-AG 2025"
  
  // Documents analysés (références GED)
  documentsIds: string[];
  
  // Résultats agrégés
  scoreConformite: number;       // 0-100
  controlesTotal: number;
  controlesOK: number;
  controlesAttention: number;
  controlesAnomalie: number;
  
  // Statut
  statut: StatutVerification;
  
  // Traçabilité
  dateCreation: Timestamp;
  dateModification: Timestamp;
  dateCloture?: Timestamp;
  createdBy: string;
  createdByNom: string;
  closedBy?: string;
  closedByNom?: string;
  
  // Export
  rapportPdfUrl?: string;        // PDF généré pour l'AG
  rapportGenereAt?: Timestamp;
}

// ============================================
// DONNÉES CRÉATION / MISE À JOUR
// ============================================

/**
 * Données pour créer une nouvelle vérification
 */
export interface CreateVerificationData {
  coproprieteId: string;
  exercice: string;
  titre?: string;
  documentsIds?: string[];
}

/**
 * Données pour mettre à jour une vérification
 */
export interface UpdateVerificationData {
  titre?: string;
  documentsIds?: string[];
  statut?: StatutVerification;
  scoreConformite?: number;
  controlesTotal?: number;
  controlesOK?: number;
  controlesAttention?: number;
  controlesAnomalie?: number;
  rapportPdfUrl?: string;
}

/**
 * Données pour créer un contrôle
 */
export interface CreateControleData {
  type: TypeControle;
  resultat: ResultatControle;
  description: string;
  regleAppliquee: string;
  montant?: number;
  montantAttendu?: number;
  ecart?: number;
  pourcentageEcart?: number;
  fournisseur?: string;
  libelle?: string;
  dateDocument?: Date;
  documentSourceId?: string;
}

/**
 * Données pour traiter un contrôle (action CS)
 */
export interface TraiterControleData {
  statut: StatutControle;
  noteCS?: string;
  justificatifUrl?: string;
  courrierId?: string;
  affaireId?: string;
}

// ============================================
// RÉSUMÉ POUR DASHBOARD
// ============================================

/**
 * Résumé d'une vérification pour affichage liste
 */
export interface VerificationResume {
  id: string;
  exercice: string;
  titre?: string;
  scoreConformite: number;
  controlesOK: number;
  controlesAttention: number;
  controlesAnomalie: number;
  statut: StatutVerification;
  dateCreation: Timestamp;
  nombreDocuments: number;
  // Session C — compteurs cockpit
  nombreAppelsFonds?: number;
  montantTotalAppele?: number;
  montantTotalRegle?: number;
  aRegularisation?: boolean;
  notes?: string;
}

// ============================================
// EXTRACTION COMPTABLE (pour SENTINELLE)
// ============================================

/**
 * Ligne extraite d'un document comptable
 */
export interface LigneComptable {
  libelle: string;
  montant: number;
  dateOperation?: Date;
  categorie?: string;
  fournisseur?: string;
  reference?: string;
}

/**
 * Résultat d'extraction d'un document comptable par SENTINELLE
 */
export interface ExtractionComptable {
  documentId: string;
  typeDocument: 'annexe' | 'appel_fonds' | 'budget' | 'releve' | 'facture';
  exercice?: string;
  dateDocument?: Date;
  
  // Données extraites
  lignes: LigneComptable[];
  totaux: {
    general?: number;
    parCategorie?: Record<string, number>;
  };
  
  // Métadonnées extraction
  confiance: number;             // 0-100
  avertissements?: string[];     // Problèmes détectés lors de l'extraction
  extractionAt: Timestamp;
}

// ============================================
// Session 263 : CLASSIFICATION CONTRÔLES
// ============================================

/**
 * Contrôles "techniques" — incohérences internes au document du syndic.
 * L'utilisateur ne peut pas les corriger lui-même.
 * Affichés en accordéon discret, n'impactent pas le statut principal.
 */
export const CONTROLES_TECHNIQUES: TypeControle[] = [
  'total_vs_detail',
  'repartition_tantiemes',
  'report_nouveau',
  'releve_vs_compta',
  'facture_vs_compta',
  'budget_variation',
  'fournisseur_unique',
  'provision_non_utilisee',
];

/**
 * Contrôles "utilisateur" — points qui appellent une action ou une vigilance.
 * Affichés en premier, clairement expliqués.
 */
export const CONTROLES_UTILISATEUR: TypeControle[] = [
  'appel_vs_budget',
  'doublon_facture',
  'dates_exercice',
  'contrat_vs_factures',
  'resolution_vs_depense',
  'surfacturation',
  'travaux_non_votes',
  'honoraires_excessifs',
  'impayes_non_recouvres',
];

/**
 * Statut lisible de la vérification — remplace le score 0-100.
 * Calculé à partir des contrôles utilisateur uniquement.
 */
export type StatutConformite = 'a_jour' | 'point_examiner' | 'action_requise' | 'non_analyse';

export const STATUT_CONFORMITE_LABELS: Record<StatutConformite, string> = {
  a_jour: 'À jour',
  point_examiner: 'Point à examiner',
  action_requise: 'Action requise',
  non_analyse: 'Non analysé',
};

export const STATUT_CONFORMITE_COLORS: Record<StatutConformite, string> = {
  a_jour: 'bg-emerald-100 text-emerald-800',
  point_examiner: 'bg-amber-100 text-amber-800',
  action_requise: 'bg-red-100 text-red-800',
  non_analyse: 'bg-gray-100 text-gray-600',
};

/**
 * Calcule le statut lisible à partir des contrôles (filtrés sur CONTROLES_UTILISATEUR).
 */
export function calculerStatutConformite(controles: { type: TypeControle; resultat: ResultatControle; statut: StatutControle }[]): StatutConformite {
  if (controles.length === 0) return 'non_analyse';
  const pertinents = controles.filter(c => CONTROLES_UTILISATEUR.includes(c.type));
  if (pertinents.length === 0) return 'a_jour';
  const nonTraites = pertinents.filter(c => c.statut === 'nouveau');
  if (nonTraites.some(c => c.resultat === 'anomalie')) return 'action_requise';
  if (nonTraites.some(c => c.resultat === 'attention')) return 'point_examiner';
  return 'a_jour';
}

// ============================================
// CONSTANTES SEUILS
// ============================================

export const SEUILS_CONTROLE = {
  // Variation budget acceptable sans alerte
  VARIATION_BUDGET_ACCEPTABLE: 0.20,  // 20%
  
  // Honoraires hors forfait excessifs
  HONORAIRES_HORS_FORFAIT_SEUIL: 0.30, // 30% du forfait
  
  // Seuil travaux nécessitant vote AG
  TRAVAUX_SEUIL_VOTE: 1000, // 1000€
  
  // Écart acceptable entre facture et compta
  ECART_FACTURE_ACCEPTABLE: 0.01, // 1%
  
  // Ancienneté impayés pour alerte
  IMPAYES_ANCIENNETE_JOURS: 180, // 6 mois
} as const;

// ============================================
// HELPERS
// ============================================

/**
 * Calcule le score de conformité à partir des compteurs
 */
export function calculerScoreConformite(
  controlesOK: number,
  controlesAttention: number,
  controlesAnomalie: number
): number {
  const total = controlesOK + controlesAttention + controlesAnomalie;
  if (total === 0) return 100;
  
  // OK = 100%, Attention = 50%, Anomalie = 0%
  const score = ((controlesOK * 100) + (controlesAttention * 50)) / total;
  return Math.round(score);
}

/**
 * Détermine la couleur du score
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

/**
 * Détermine le libellé du score
 */
export function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent';
  if (score >= 80) return 'Bon';
  if (score >= 60) return 'À surveiller';
  if (score >= 40) return 'Problématique';
  return 'Critique';
}

// ============================================================================
// SESSION C — TYPES COCKPIT COMPTABLE
// ============================================================================

export type TrimestreAppel = 'T1' | 'T2' | 'T3' | 'T4' | 'regularisation' | 'exceptionnel';

export type StatutPaiement = 'a_regler' | 'regle' | 'en_attente' | 'conteste';

export type NatureFiscale =
  | 'deductible_foncier'
  | 'deductible_lmnp'
  | 'amortissable_lmnp'
  | 'non_deductible'
  | 'a_determiner';

export interface LigneFiscale {
  libelle: string;
  montant: number;
  natureFiscale: NatureFiscale;
  commentaire?: string;
}

export interface VentilationFiscale {
  totalDeductible: number;
  totalAmortissable: number;
  totalNonDeductible: number;
  detailParLigne: LigneFiscale[];
  statut: 'LMNP' | 'SCI_IS' | 'SCI_IR' | 'foncier_reel' | 'micro_foncier';
  avertissements: string[];
}

export interface VentilationPoste {
  poste: string;
  libelle?: string;
  montant: number;
}

export interface AppelFonds {
  id: string;
  verificationId: string;
  coproprieteId: string;
  trimestre: TrimestreAppel;
  montantAppele: number;
  montantRegle: number;
  montantAttendu?: number;
  ecartBudget?: number;
  ventilationPostes: VentilationPoste[];
  ventilationFiscale?: VentilationFiscale | null;
  statut: StatutPaiement;
  documentId?: string;
  documentNom: string;
  dateEcheance?: any;
  datePaiement?: any;
  note?: string;
  createdAt: any;
  updatedAt: any;
  creePar?: string;
}

export interface CreateAppelFondsData {
  verificationId: string;
  coproprieteId: string;
  trimestre: TrimestreAppel;
  montantAppele: number;
  montantAttendu?: number;
  ecartBudget?: number;
  ventilationPostes: VentilationPoste[];
  ventilationFiscale?: VentilationFiscale | null;
  documentId?: string;
  documentNom: string;
  dateEcheance?: any;
}

export interface Regularisation {
  id: string;
  verificationId: string;
  coproprieteId: string;
  exerciceRegularise: string;
  exerciceReception: string;
  montantProvisionne: number;
  montantReel: number;
  delta: number;
  estTropPercu: boolean;
  ventilationFiscale?: VentilationFiscale | null;
  statut: StatutPaiement;
  documentId?: string;
  documentNom: string;
  datePaiement?: any;
  note?: string;
  createdAt: any;
  updatedAt: any;
}

export interface CreateRegularisationData {
  verificationId: string;
  coproprieteId: string;
  exerciceRegularise: string;
  exerciceReception: string;
  montantProvisionne: number;
  montantReel: number;
  delta: number;
  estTropPercu: boolean;
  ventilationFiscale?: VentilationFiscale | null;
  documentId?: string;
  documentNom: string;
}

export interface SyntheseComptable {
  totalAppele: number;
  totalRegle: number;
  totalEnAttente: number;
  appelsByTrimestre: Record<string, { appele: number; regle: number }>;
  regularisationEnCours?: { montant: number; estTropPercu: boolean };
  soldeFinal: number;
  scoreConformite: number;
  syntheseFiscale?: {
    totalDeductible: number;
    totalAmortissable: number;
    totalNonDeductible: number;
    avertissements: string[];
    statut: string;
  };
}

export interface PropositionAppelFonds {
  documentId: string;
  documentNom: string;
  trimestre: string;
  exercice: number;
  montantAppele: number;
  dateAppel: string | null;
}

export interface PropositionRegularisation {
  documentId: string;
  documentNom: string;
  exerciceRegularise: number;
  montantReel: number;
  estTropPercu: boolean;
}

export interface ResultatAnalyseComptable {
  success: boolean;
  documentsAnalyses: number;
  controlesAjoutes: number;
  appelsFondsCreees: number;
  regularisationsCreees: number;
  erreurs: string[];
  tokensUtilises?: number;
  propositionsAppelsFonds?: PropositionAppelFonds[];
  propositionsRegularisations?: PropositionRegularisation[];
}

// ── Labels et couleurs ────────────────────────────────────────────────────────

export const STATUT_PAIEMENT_LABELS: Record<StatutPaiement, string> = {
  a_regler: 'À régler',
  regle: 'Réglé',
  en_attente: 'En attente',
  conteste: 'Contesté',
};

export const STATUT_PAIEMENT_COLORS: Record<StatutPaiement, string> = {
  a_regler: 'bg-amber-100 text-amber-800',
  regle: 'bg-green-100 text-green-800',
  en_attente: 'bg-gray-100 text-gray-700',
  conteste: 'bg-red-100 text-red-800',
};

export const TRIMESTRE_LABELS: Record<TrimestreAppel, string> = {
  T1: '1er trimestre',
  T2: '2ème trimestre',
  T3: '3ème trimestre',
  T4: '4ème trimestre',
  regularisation: 'Régularisation',
  exceptionnel: 'Exceptionnel',
};

export const NATURE_FISCALE_LABELS: Record<NatureFiscale, string> = {
  deductible_foncier: 'Déductible (foncier)',
  deductible_lmnp: 'Déductible (LMNP)',
  amortissable_lmnp: 'Amortissable',
  non_deductible: 'Non déductible',
  a_determiner: 'À déterminer',
};

export const NATURE_FISCALE_COLORS: Record<NatureFiscale, string> = {
  deductible_foncier: 'bg-green-100 text-green-800',
  deductible_lmnp: 'bg-green-100 text-green-800',
  amortissable_lmnp: 'bg-blue-100 text-blue-800',
  non_deductible: 'bg-red-100 text-red-800',
  a_determiner: 'bg-gray-100 text-gray-600',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function detecterTrimestre(date: Date): TrimestreAppel {
  const mois = date.getMonth() + 1;
  if (mois <= 3) return 'T1';
  if (mois <= 6) return 'T2';
  if (mois <= 9) return 'T3';
  return 'T4';
}

export function formatMontant(montant: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(montant);
}
